import { createGeneralPageEventsAndComponents } from "../technical/page-general";
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll, expressRemoveClass, getParentWithClassName, hasClass, htmlToElement
} from "../../common/html";
import { createCustomSlider, ICustomPhotoSliderItem, ICustomPhotoSliderWithDivSettings } from "../technical/customslider";
import { postRequest } from "../../utils/fetch";
import { getParameter, updateParameter } from "../../utils/querystring";
import { getProductCodeFromPvc, getOrientationFromPvc } from "../../utils/product";
import { createProductGrid } from "../molecules/product-grid";
import { initPagedShowMoreElements, ShowMoreClickedReturn } from "../technical/show-more-elements";
import { showLoadingDialog } from "../popups/loading-dialog";
import { createDesignSelectionPageDialog, IDesignSelectionPageDialog } from "../popups/design-selection-page";
import { createEmptyState, IEmptyState } from "../molecules/empty-state";
import { getDesign } from "../../services/design-service";
import { createPromotionBar } from "../organisms/promotion-bar";
import { getCookie } from "../../utils/cookie";
import { sortGroupFilterValues } from "../organisms/filters";
import { loadSvgIcons } from "../../utils/svg";

enum SearchSortColumns { OrderCounts, Top, New, PriceHighLow, PriceLowHigh }

export interface IDspSearchRequest {
	currentPage: number,
	forWho?: string[],
	forWhat?: string[],
	forWhen?: string[],
	orientation?: string[],
	predefinedProductCategory?: string[], // DesignStyle
	searchColor?: string[],
	minImageCount?: number,
	maxImageCount?: number,
	minPrice?: number,
	maxPrice?: number,
	productCode?: string[],
	mainProductCategoryGroupCode?: string[],
	designGroup?: string,
	theme?: string,
	sortMethod?: SearchSortColumns,
	promoCode?: string,
	dspEntrance: number
}
window.addEventListener('DOMContentLoaded', () => {
	window.showMores = [];
	const validateQueryString = (): { productVariant?: string, themeContentKey?: string, designGroup?: string } => {
		const designGroup = getParameter('dg') ?? '';
		if (designGroup) return { designGroup: designGroup };

		return {
			productVariant: getParameter('pvc') ?? '',
			themeContentKey: (getParameter('theme') ?? '').split(',')[0]
		};
	};

	const dspContainerEl = expressQuerySelector<HTMLElement>(document, '.technical-dsp', true);
	const dspEntrance = parseInt(dspContainerEl.dataset.entrance, 10);
	const promoBar = expressQuerySelector<HTMLElement>(document, '.technical-promotion-bar', false);
	const body = expressQuerySelector(document, "body", true);
	const timeoutBeforeShowingLoadingDialog = parseInt(body.dataset.timeoutbeforeshowingloadingdialog ||"200", 10);
	const timeoutBeforeShowingSkeletonScreen = parseInt(body.dataset.timeoutbeforeshowingskeletonscreen ||"200", 10);
	createGeneralPageEventsAndComponents();
	promoBar && createPromotionBar(promoBar);
	const popups: IDesignSelectionPageDialog[] = [];
	const sliderContainer = expressQuerySelector(document, '.technical-sliders-container');
	const { themeContentKey = '', productVariant= '', designGroup = '' } = validateQueryString();
	let productCode = getProductCodeFromPvc(productVariant);
	let orientation = getOrientationFromPvc(productVariant);
	const isMobile = window.innerWidth <= 767;
	let emptyState: IEmptyState;

	const themeUsedForDesigns = dspContainerEl.dataset.themeusedfordesigns;

	const updateGridTitle = (productName: string) => {
		const productGridTitle = expressQuerySelector<HTMLElement>(dspContainerEl, '.technical-grid-title', false);
		if (productGridTitle) {
			const titleLabel = productGridTitle.dataset.titlelabel || '';
			if (titleLabel && titleLabel.includes("{0}") && productName) {
				productGridTitle.innerHTML = titleLabel.replace("{0}", productName);
			}
		}
	};

	const heightScriptButtons = (slider, height) => {
		// BUTTONS
		const sliderButtons = expressQuerySelectorAll<HTMLElement>(slider, '.technical-slider-button');
		const bottom = height / 2;
		const buttonHeightAdjust = 18;
		for (let i = 0, l = sliderButtons.length; i < l; i++) {
			sliderButtons[i].style.top = `${bottom - buttonHeightAdjust}px`;
		}
	};

	const initPvcSlider = () => {
		if (!sliderContainer)
			return;

		// init sliders
		const sliders = expressQuerySelectorAll<HTMLElement>(document, '.technical-slider');
		sliders.forEach(slider => {
			const sliderElements: ICustomPhotoSliderItem[] = [];
			for (let z = 0, l = slider.children.length; z < l; z++)
				sliderElements.push({ template: slider.children[z] as HTMLElement });

			const sliderSettings: ICustomPhotoSliderWithDivSettings = {
				sliderElements: sliderElements,
				sliderContainerSettings: {
					extraSliderClass: 'm-product-slider__slider-container',
					elementsContainerSettings: {
						extraSliderClass: slider.dataset.sliderelementsextraclass,
						slideClicked: (e: Event, settings: ICustomPhotoSliderWithDivSettings) => {
							const slideToActivate = ((e.target as HTMLElement).closest('.technical-slider-element') as HTMLElement);
							if(!slideToActivate) // when clicked between slides, their is no slide to activate
								return;

							const pvc = slideToActivate.dataset['pvc'];
							const productGridTitleReplacement = slideToActivate.dataset['productgridtitlereplacement'];
							updateGridTitle(productGridTitleReplacement);
							productCode = getProductCodeFromPvc(pvc);
							orientation = getOrientationFromPvc(pvc);
							productGrid.reFilter(productGridEl);

							const slideElements = (e.target as HTMLElement).closest('.technical-slider-elements');
							if (!slideToActivate || !slideElements || slideElements.children.length <= 0) return;
							for (let i = 0; i < slideElements.children.length; i++) {
								slideElements.children[i].classList.toggle('m-product-slider__slide--active', slideElements.children[i] === slideToActivate);
							}
						}
					},
					nextButton: {
						onClick: (_: Event, draggedSlider: boolean) => { return; }
					},
					previousButton: {
						onClick: (_: Event, draggedSlider: boolean) => { return; }
					}
				},
				SliderCreated: (settings, initializedSlider, oldSlider) => {
					const firstSlide = expressQuerySelectorAll<HTMLElement>(slider, '.technical-slide')[0];
					const height = expressQuerySelector<HTMLElement>(firstSlide, '.m-product__link-container').clientHeight;
					heightScriptButtons(initializedSlider, height);

					// Set PVC to the one selected
					const activeSlides = expressQuerySelectorAll<HTMLElement>(initializedSlider, '.m-product-slider__slide--active');
					const pvc = activeSlides[0].dataset.pvc;
					if (pvc) {
						productCode = getProductCodeFromPvc(pvc);
						orientation = getOrientationFromPvc(pvc);
					}
				},
				ResizeEvent: (settings, slider) => { return; }
			};
			createCustomSlider(slider, sliderSettings);
		});
	};

	const productContentEl = expressQuerySelector<HTMLElement>(dspContainerEl, '.m-product-grid__content', true);

	const productTempUnavailableEls = expressQuerySelectorAll<HTMLElement>(productContentEl, ".m-product--temporarily-unavailable");
	productTempUnavailableEls.forEach(el => {
		const icons = el.querySelectorAll(".technical-icon");
		icons.forEach(icon => {
			icon.classList.add("a-icon--unavailable");
		});
	});

	const productGridEl = expressQuerySelector<HTMLElement>(dspContainerEl, '.technical-product-grid');
	const showMoreElements = (currentPage: number, sizeDesktop = null, sizeMobile = null, sortMethod: string = null, selectedFilters: { [inputName: string]: string[] }): Promise<ShowMoreClickedReturn> => {
		if (currentPage == 1) {
			const showMoreButton = expressQuerySelector(productGridEl, '.technical-showmore-button');
			const allShown = showMoreButton.classList.contains('u-hide-on-desktop-full') || showMoreButton.classList.contains('u-hide-on-mobile-full');
			return Promise.resolve({ allShownDesktop: allShown, allShownMobile: allShown } as ShowMoreClickedReturn);
		}

		const price = selectedFilters ? selectedFilters['Pr'] : null;
		let splitPrice: string[] = null;
		if (price && price.length > 0) {
			splitPrice = (price[0].split('_')[1] || price[0]).split('-');
		}

		const imageCount = selectedFilters && selectedFilters['Ic'];
		const splitImageCount = imageCount && (imageCount[imageCount.length - 1]?.split('_')[1] || imageCount[imageCount.length - 1])?.split('-');

		return showLoadingDialog(postRequest<IDspSearchRequest, { allShownDesktop: boolean, allShownMobile: boolean, tiles: string }>('/apicore/dspapi/getdesigns',
			{
				currentPage: currentPage,
				theme: themeUsedForDesigns,
				forWho: selectedFilters ? selectedFilters['ForWho'] : null,
				forWhat: selectedFilters ? selectedFilters['ForWhat'] : null,
				forWhen: selectedFilters ? selectedFilters['ForWhen'] : null,
				designGroup: designGroup,
				productCode: [productCode],
				predefinedProductCategory: selectedFilters ? selectedFilters['Ppc'] : null,
				mainProductCategoryGroupCode: selectedFilters ? selectedFilters['Pcg'] : null,
				searchColor: selectedFilters ? selectedFilters['Sc'] : null,
				maxImageCount: splitImageCount ? +(splitImageCount[1] || splitImageCount[0]) : null,
				minImageCount: splitImageCount ? +(splitImageCount[0]) : null,
				maxPrice: splitPrice ? parseInt(splitPrice[1]) : null,
				minPrice: splitPrice ? parseInt(splitPrice[0]) : null,
				orientation: [orientation],
				sortMethod: sortMethodToApiSortMethod(sortMethod || getParameter('sortmethod[0]')),
				promoCode: getParameter("epc") || getCookie("promo"),
				dspEntrance: dspEntrance
			},
			{ headers: { 'Content-type': 'application/json; charset=utf-8' } }
		).then(x => {
			const parser = new DOMParser();
			const doc = parser.parseFromString(x.tiles, 'text/html');

			const productTiles = expressQuerySelector(dspContainerEl, '.technical-product-grid-content');
			doc.body.childNodes.forEach(x => {
				productTiles.appendChild(x);
				const iconEls = expressQuerySelectorAll<HTMLElement>(x as HTMLElement, '.technical-icon');
				loadSvgIcons(iconEls);
				addTileOnClick(x as HTMLElement);
			});

			updateFromLabelInYourSelection(Array.from(doc.body.children));

			return {
				allShownDesktop: x.allShownDesktop,
				allShownMobile: x.allShownMobile,
			} as ShowMoreClickedReturn;
		}), timeoutBeforeShowingLoadingDialog);
	};

	const addTileOnClick = (el: HTMLElement) => {
		const links = expressQuerySelectorAll(el, `a[href='#'],.technical-icon`);
		links.forEach(x => {
			expressEventListener(x, 'click', (e) => el.dataset.tempunavailable === 'false' ? onDetailsPopupTriggerElClick(e, el) : e.preventDefault());
		});
	};

	const sortMethodToApiSortMethod = (method: string) => {
		switch (method) {
			case 'default': return SearchSortColumns.OrderCounts;
			case 'price-high-low': return SearchSortColumns.PriceHighLow;
			case 'price-low-high': return SearchSortColumns.PriceLowHigh;
			case 'top': return SearchSortColumns.Top;
			case 'new': return SearchSortColumns.New;
			default: return undefined;
		}
	};

	// { allShownDesktop: boolean, allShownMobile: boolean, tiles: string }
	const getDesignTiles = (body: IDspSearchRequest) => postRequest<IDspSearchRequest, any[]>('/apicore/dspapi/getdesigns', body, { headers: { 'Content-type': 'application/json; charset=utf-8' } });
	const getFilters = (body: IDspSearchRequest) => postRequest<IDspSearchRequest, any[]>('/apicore/dspapi/getfilters', body, { headers: { 'Content-type': 'application/json; charset=utf-8' } });
	const calculateHackFor2TilesOnLastLine = (grid: HTMLElement): void => {
		const filters = expressQuerySelector(grid.parentElement, '.technical-filters-container', false);
		const tiles = expressQuerySelectorAll(grid, '.technical-tile:not(.u-hide-on-desktop-full):not(.u-hide)');

		if (tiles.length % (filters ? 3 : 4) == 2)
			grid.classList.add('m-product-grid--tail-2-tiles-on-desktop');
		else
			grid.classList.remove('m-product-grid--tail-2-tiles-on-desktop');
	};

	const filterProductGrid = (grid: HTMLElement, filterName: string = null, sortMethod: string = null, selectedFilters: { [inputName: string]: string[] } = null) => {
		selectedFilters = selectedFilters || {};
		const button = expressQuerySelector<HTMLElement>(dspContainerEl, '.technical-showmore-button', false);

		const reset = filterName === 'reset' || (selectedFilters && (Object.keys(selectedFilters).every(x => !selectedFilters[x] || selectedFilters[x].length === 0) || Object.keys(selectedFilters).length === 0));
		if (reset && !sortMethod) {
			history.replaceState(null, '', updateParameter('currentpage[0]', ''));
			button.dataset.currentpage = '1';
		}

		const urlParam = parseInt(getParameter('currentpage[0]'));
		const currentPage = isNaN(urlParam) ? 1 : urlParam;
		const imageCount = selectedFilters['Ic'];
		const splitImageCount = imageCount && (imageCount[imageCount.length - 1]?.split('_')[1] || imageCount[imageCount.length - 1])?.split('-');
		const priceFilter = selectedFilters['Pr'];
		const price = priceFilter && (priceFilter[priceFilter.length - 1]?.split('_')[1] || priceFilter[priceFilter.length - 1])?.split('-');

		const dspData = {
			// Stuff from JS variables
			currentPage: currentPage,

			// Stuff from querystring that never changes
			designGroup: getParameter('dg'),
			theme: themeUsedForDesigns,

			// Stuff from filters that changes each click
			forWho: selectedFilters['ForWho'],
			forWhat: selectedFilters['ForWhat'],
			forWhen: selectedFilters['ForWhen'],
			mainProductCategoryGroupCode: selectedFilters['Pcg'],
			orientation: [orientation],
			maxImageCount: splitImageCount ? +(splitImageCount[1] || splitImageCount[0]) : null,
			minImageCount: splitImageCount ? +splitImageCount[0] : null,
			maxPrice: +(price && price[1]),
			minPrice: +(price && price[0]),
			// theme: getParameter('theme'),
			searchColor: selectedFilters['Sc'],
			productCode: [productCode],
			predefinedProductCategory: selectedFilters['Ppc'],
			sortMethod: sortMethodToApiSortMethod(sortMethod || getParameter('sortmethod[0]')),
			promoCode: getParameter("epc") || getCookie("promo"),
			dspEntrance: dspEntrance
		} as IDspSearchRequest;

		// reset button
		button && button.classList.toggle('u-hide', true);
		button && button.parentElement && button.parentElement.classList.toggle('u-hide-on-desktop-full', true);
		button && button.parentElement && button.parentElement.classList.toggle('u-hide-on-mobile-full', true);

		const designCount = expressQuerySelector<HTMLElement>(dspContainerEl, '.technical-amount-of-tiles');

		// replace tiles
		const productTiles = expressQuerySelector(grid, '.technical-product-grid-content');
		productTiles.innerHTML = '';
		emptyState && emptyState.dispose();
		const parser = new DOMParser();

		// reset filters/ designs
		const pageArray = Array.from({ length: currentPage }, (_, i) => i + 1);
		showLoadingDialog(Promise.all([getFilters(dspData), ...pageArray.map(i => { dspData.currentPage = i; return getDesignTiles(dspData); })]).then(result => {
			// replace count filters
			const filters = result.shift();
			if (!filters) return;
			let totalTiles = 0;
			const htmlFilters = expressQuerySelectorAll<HTMLElement>(dspContainerEl, '.technical-filter');
			htmlFilters.forEach(htmlFilter => {
				const filter = filters.find(x => htmlFilter.dataset.filterqs === x.code);
				const htmlFilterValues = expressQuerySelectorAll<HTMLElement>(htmlFilter, '.technical-filter-value');

				htmlFilterValues.forEach(htmlFilterValue => {
					const filterValue = filter ? filter.values.find(fv => htmlFilterValue.dataset.filterkey.toLowerCase() === fv.key.toLowerCase()) || { count: 0 } : { count: 0 };

					const label = expressQuerySelector<HTMLElement>(htmlFilterValue, 'label', false);
					const labelText = label && expressQuerySelector<HTMLElement>(label, '.technical-label', false);
					const input = expressQuerySelector<HTMLInputElement>(htmlFilterValue, 'input', false);
					const icon = expressQuerySelector<HTMLInputElement>(htmlFilterValue, 'i', false);

					if (labelText) {
						const indexCount = labelText.textContent.lastIndexOf('(');
						labelText.textContent = labelText.textContent.substring(0, indexCount) + `(${filterValue.count})`;
					}

					const shouldFilterBeDisabled = ((input && !input.checked) || (icon && !htmlFilterValue.classList.contains('v2-a-icon--selected'))) && filterValue.count === 0;
					htmlFilterValue.classList.toggle(icon ? 'v2-a-icon--disabled' : 'a-form-input--disabled', shouldFilterBeDisabled);
					input && input.toggleAttribute('disabled', shouldFilterBeDisabled);
				});

				// WEB-9628: Group disabled filter values and move them to the end of de list
				if (htmlFilter.dataset.typeoffilter !== "Slider") sortGroupFilterValues(htmlFilterValues, htmlFilter);
			});

			// add tiles paged
			result.sort(r => (r as any).currentPage).forEach(res => {
				const { tiles, allShownDesktop, allShownMobile, count } = res as any;
				totalTiles = count;
				const doc = parser.parseFromString(tiles, 'text/html');
				const productTiles = expressQuerySelector(dspContainerEl, '.technical-product-grid-content');
				if (doc.body.childNodes.length > 0) {
					emptyState && emptyState.hide();
					doc.body.childNodes.forEach(x => {
						productTiles.appendChild(x);
						const iconEls = expressQuerySelectorAll<HTMLElement>(x as HTMLElement, '.technical-icon');
						loadSvgIcons(iconEls);
						addTileOnClick(x as HTMLElement);
					});
				}

				// toggle show more
				button && button.classList.toggle('u-hide', allShownDesktop && allShownMobile);
				button && button.parentElement && button.parentElement.classList.toggle('u-hide-on-desktop-full', allShownDesktop);
				button && button.parentElement && button.parentElement.classList.toggle('u-hide-on-mobile-full', allShownMobile);
				button && button.parentElement && button.parentElement.classList.toggle('u-hide', allShownDesktop && allShownMobile);

				// update count
				if (designCount) {
					designCount.innerHTML = count > 1
						? designCount.dataset['multi'].replace("{0}", count)
						: designCount.innerHTML = designCount.dataset['single'];
					designCount.classList.toggle('u-hide', count <= 0);
				}

				updateFromLabelInYourSelection(Array.from(doc.body.children));
			});

			if (emptyStateEl && totalTiles === 0) {
				productTiles.appendChild(emptyStateEl);
				emptyState.dispose();
				emptyState = createEmptyState(emptyStateEl, { onResetButtonClick: productGrid.resetFilter, onShow: () => productGrid.disableSort(true), onHide: () => productGrid.disableSort(false) });
				emptyState.show();
			}

			if (sortMethod) {
				let url = location.href;
				if (sortMethod === 'default')
					url = updateParameter('sortmethod[0]', '', url);
				else
					url = updateParameter('sortmethod[0]', sortMethod, url);
				if (url != location.href)
					history.replaceState(null, '', url.toLowerCase());
			}

			calculateHackFor2TilesOnLastLine(grid);

			if (!reset) return;

			// reset show more button
			if (window.showMores[0])
				window.showMores[0].dispose();
			window.showMores[0] = initPagedShowMoreElements(grid, button, 'currentpage', null, 0, (currentPage: number) => showMoreElements(currentPage, null, null, sortMethod, selectedFilters), () => calculateHackFor2TilesOnLastLine(grid));

		}), timeoutBeforeShowingLoadingDialog);
	};

	const updateFromLabelInYourSelection = (childNodes: Element[]) => {
		// update from in your selection
		const selectionFromEl = expressQuerySelector<HTMLElement>(document, ".technical-selection-from", false);
		if (!selectionFromEl) return;
		const currentPrice = parseInt(selectionFromEl.dataset.price || '0', 10);
		const newTilePrices = childNodes.map(el => {
			const tile = expressQuerySelector<HTMLElement>(el, '.technical-tile', true);
			return parseInt(tile.dataset.sortPrice || '0', 10);
		});
		if (hasClass(selectionFromEl, 'u-hide') && newTilePrices.filter(p => p !== currentPrice).length > 0) {
			expressRemoveClass(selectionFromEl, 'u-hide');
		}
	};

	const productGrid = createProductGrid(productGridEl, { showMoreClick: showMoreElements, filterGrid: filterProductGrid, enableShowLess: true }, { refreshOnResize: false });
	const emptyStateEl = expressQuerySelector<HTMLElement>(productContentEl, '.technical-no-elements', false);
	if (emptyStateEl) emptyState = createEmptyState(emptyStateEl, { onResetButtonClick: productGrid.resetFilter, onShow: () => productGrid.disableSort(true), onHide: () => productGrid.disableSort(false)});

	// Init the product detail popup for each tile
	const tileEls = expressQuerySelectorAll<HTMLElement>(productGridEl, '.m-product--dsp-tile');


	const onDetailsPopupTriggerElClick = async (e: MouseEvent, tile: HTMLElement) => {
		const targetEl = e.target as HTMLElement;
		e.preventDefault();
		const clickedIconEL = targetEl.classList.contains('technical-icon-alternative-color') ? targetEl : getParentWithClassName(targetEl,'technical-icon-alternative-color') as HTMLElement;
		if (clickedIconEL) {
			const removeSelectedIconItems = () => {
				const iconSelEl = getParentWithClassName(targetEl, 'technical-icon-set');
				if (iconSelEl) {
					const iconEls = expressQuerySelectorAll<HTMLElement>(iconSelEl, '.technical-icon-alternative-color');
					iconEls.forEach(iconEl => {
						expressRemoveClass(iconEl, 'v2-a-icon--selected');
						expressAddClass(iconEl, 'v2-a-icon--unselected');
					});
				}
			};
			const selectIconEl = (iconEl: HTMLElement) => {
				expressRemoveClass(iconEl, 'v2-a-icon--unselected');
				expressAddClass(iconEl, 'v2-a-icon--selected');
			};

			// Change this on the tile in order to open the popup with the selected color
			tile.dataset.ppn = clickedIconEL.dataset.predefinedproductname;
			tile.dataset.defaultoptions = clickedIconEL.dataset.defaultoptions || tile.dataset.defaultoptions || '';
			const packShotImgEl = expressQuerySelector<HTMLImageElement>(tile, 'img.m-product__img', true);
			if (clickedIconEL.dataset.packshoturl && packShotImgEl.src !== clickedIconEL.dataset.packshoturl) {
				packShotImgEl.src = clickedIconEL.dataset.packshoturl;
				removeSelectedIconItems();
				selectIconEl(clickedIconEL);
			} else {
				getDesign(tile.dataset.productcode || '', clickedIconEL.dataset.predefinedproductname || '', themeUsedForDesigns || '')
					.then(res => {
						if (res && res.packshotUrl) {
							if (packShotImgEl.src !== res.packshotUrl) {
								packShotImgEl.src = res.packshotUrl;
								removeSelectedIconItems();
								selectIconEl(clickedIconEL);
							}
							clickedIconEL.dataset.packshoturl = res.packshotUrl;
							clickedIconEL.dataset.defaultoptions = res.defaultOptions || '';
							tile.dataset.defaultoptions = res.defaultOptions || '';
						}
					});
			}
		} else {
			await openPopupForDesign(tile);
		}
	};

	const openPopupForDesign = async (tile: HTMLElement) => {
		const defaultProductCode = tile.dataset.productcode || '';
		const defaultPreDefinedProductName = tile.dataset.predefinedproductname || '';
		const productCode = getParameter('pc') || '';
		const productCategory = tile.dataset.productcategory || '';
		const preDefinedProductName = getParameter('ppn') || (tile.dataset.ppn && tile.dataset.predefinedproductname !== tile.dataset.ppn ? tile.dataset.ppn : '');
		const aspectRatio = tile.dataset.aspectratio || '';
		const designGroup = tile.dataset.designgroup || '';
		const pvc = tile.dataset.pvc || '';
		const defaultOptions = tile.dataset.defaultoptions || '';
		const packShot = expressQuerySelector<HTMLImageElement>(tile, "img.m-product__img", false);
		const packShotUri = !productCode && !preDefinedProductName && packShot ? packShot.src : '';
		const contentTheme = tile.dataset.contenttheme || '';
		const popupId = 'DesignSelectionPageDialog-' + (productCode || defaultProductCode) + '_' + productCategory + '-' + (preDefinedProductName || defaultPreDefinedProductName) + '-' + designGroup + '-' + aspectRatio + '-' + pvc;
		let popup = popups.find(p => p.getId() === popupId);
		updateUrl(defaultProductCode, defaultPreDefinedProductName, productCode, preDefinedProductName, defaultOptions);
		if (popup) {
			popup.resetOtherFormatSpecificClass();
			popup.open();
		} else {
			popup = await showLoadingDialog(createDesignSelectionPageDialog(defaultProductCode, productCode, productCategory, defaultPreDefinedProductName, preDefinedProductName, aspectRatio, designGroup, packShotUri, contentTheme, pvc, timeoutBeforeShowingSkeletonScreen, parseInt(dspContainerEl.dataset.entrance || '0')), timeoutBeforeShowingLoadingDialog);
			popups.push(popup);
			popup.open();
		}
	};

	const updateUrl = (defaultProductCode: string, defaultPreDefinedProductName: string, productCode: string, preDefinedProductName: string, defaultOptions: string) => {
		let url = window.location.href;
		url = updateParameter('dt', defaultProductCode + '|' + defaultPreDefinedProductName, url);
		url = updateParameter('pc', productCode, url);
		url = updateParameter('ppn', preDefinedProductName, url);
		url = updateParameter('options', defaultOptions || '', url);
		window.history.replaceState(null, null, url);
	};

	tileEls.forEach(el => {
		const iconEls = expressQuerySelectorAll<HTMLElement>(el, '.technical-icon');
		loadSvgIcons(iconEls);
		addTileOnClick(el);
	});

	// Init slider if a theme entrance
	if (themeContentKey && !productVariant && !designGroup)
		initPvcSlider();

	// try open popup from url params
	const defaultTile = getParameter('dt');
	const dtItems = defaultTile ? decodeURI(defaultTile).split('|') : [];
	const defaultProductCode = dtItems[0]?.toLocaleLowerCase() || decodeURI(getParameter('cpc'))?.toLocaleLowerCase();
	const defaultPreDefinedProductName = dtItems[1]?.toLocaleLowerCase() || decodeURI(getParameter('cppn'))?.toLocaleLowerCase();
	const designToOpenInPopup = expressQuerySelector<HTMLElement>(productGridEl, `[data-productcode='${defaultProductCode}'][data-predefinedproductname='${defaultPreDefinedProductName}']`, false);
	if (designToOpenInPopup) {
		openPopupForDesign(designToOpenInPopup);
	}
});
